import type { Census } from "../types/chargingStationDashboard";

/**
 * BOF-76 — every figure the dashboard derives rather than reads.
 *
 * The API returns counts and sums and deliberately no ratios, so each guard lives in exactly one place:
 * here. The rule is uniform — a ratio with an absent or zero denominator is `null`, never `0`, `Infinity`
 * or `NaN`, and the screen renders `null` as "—". `tsconfig` has `strict: false`, so nothing in the type
 * system enforces this; the tests do.
 */

/** `numerator / denominator`, or `null` when either is absent or the denominator is zero. */
export const safeDivide = (
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null => {
  if (numerator == null || denominator == null || denominator === 0) return null;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : null;
};

/** Share of charge points online (FR-006). `null` without a census. */
export const onlineRate = (census: Census | null | undefined): number | null =>
  census ? safeDivide(census.online, census.total) : null;

/** A per-day average over the days that have elapsed — never the period's full length when it is running. */
export const perDay = (value: number | null | undefined, daysElapsed: number): number | null =>
  safeDivide(value, daysElapsed);

/** Energy per day per online charge point (FR-013). */
export const perDayPerOnlineChargePoint = (
  energyKwh: number,
  daysElapsed: number,
  census: Census | null | undefined,
): number | null => safeDivide(perDay(energyKwh, daysElapsed), census?.online);

/** Energy per session, over sessions that delivered energy (FR-020, FR-021). */
export const perSession = (energyKwh: number, qualifyingSessions: number): number | null =>
  safeDivide(energyKwh, qualifyingSessions);

/** Share of sessions that were free of charge (FR-017). */
export const freeShare = (free: number, total: number): number | null => safeDivide(free, total);

export interface Change {
  /** `current − previous`, or `null` when either is absent. */
  absolute: number | null;
  /**
   * `(current − previous) / previous` on the period *totals* (FR-040, FR-041), or `null` when the
   * previous value is absent or zero — a percentage against zero is meaningless, but the absolute change
   * is still shown (FR-039).
   */
  percent: number | null;
}

export const change = (current: number | null | undefined, previous: number | null | undefined): Change => ({
  absolute: current == null || previous == null ? null : current - previous,
  percent: current == null ? null : safeDivide(current - (previous ?? 0), previous),
});

// ── Formatting ───────────────────────────────────────────────────────────────

export const DASH = "—";

const numberFormat = (fractionDigits: number) =>
  new Intl.NumberFormat("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: fractionDigits });

export const formatNumber = (value: number | null | undefined, fractionDigits = 0): string =>
  value == null ? DASH : numberFormat(fractionDigits).format(value);

/** A 0–1 ratio as a percentage. */
export const formatPercent = (ratio: number | null | undefined, fractionDigits = 1): string =>
  ratio == null ? DASH : `${numberFormat(fractionDigits).format(ratio * 100)}%`;

/** A change as "+12.3%" / "−4.0%", signed so direction is never inferred from colour alone. */
export const formatSignedPercent = (ratio: number | null | undefined, fractionDigits = 1): string => {
  if (ratio == null) return DASH;
  const pct = ratio * 100;
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "±";
  return `${sign}${numberFormat(fractionDigits).format(Math.abs(pct))}%`;
};

export const formatSignedNumber = (value: number | null | undefined, fractionDigits = 0): string => {
  if (value == null) return DASH;
  const sign = value > 0 ? "+" : value < 0 ? "−" : "±";
  return `${sign}${numberFormat(fractionDigits).format(Math.abs(value))}`;
};

export type EnergyUnit = "kWh" | "MWh";

/**
 * One unit per panel (FR-015): MWh once the largest value in the panel reaches 10,000 kWh, kWh
 * otherwise. Chosen from every value the panel shows, so two figures side by side are never in
 * different units.
 */
export const energyUnitFor = (valuesKwh: Array<number | null | undefined>): EnergyUnit =>
  Math.max(0, ...valuesKwh.map((v) => Math.abs(v ?? 0))) >= 10_000 ? "MWh" : "kWh";

export const inUnit = (kwh: number | null | undefined, unit: EnergyUnit): number | null =>
  kwh == null ? null : unit === "MWh" ? kwh / 1000 : kwh;

export const formatEnergy = (kwh: number | null | undefined, unit: EnergyUnit): string => {
  const v = inUnit(kwh, unit);
  return v == null ? DASH : `${formatNumber(v, unit === "MWh" ? 2 : 0)} ${unit}`;
};

/** Whole euros by default; pass `fractionDigits` for small per-unit figures such as a fee per client. */
export const formatEuro = (cents: number | null | undefined, fractionDigits = 0): string =>
  cents == null
    ? DASH
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(cents / 100);

/** A change in cents, signed: "+€1,200" / "−€300". */
export const formatSignedEuro = (cents: number | null | undefined): string => {
  if (cents == null) return DASH;
  const sign = cents > 0 ? "+" : cents < 0 ? "−" : "±";
  return `${sign}${formatEuro(Math.abs(cents))}`;
};
